import { environment } from "src/environments/environment";

/**
 * A supporter's sign-in, kept completely apart from the admin one.
 *
 * Separate storage keys, separate refresh, separate everything. A club
 * employee has both in the same browser, and a fan token that could be
 * mistaken for an admin token — or the reverse — is a hole, not an
 * inconvenience. Nothing here ever reads the admin session and nothing in the
 * admin app reads this.
 *
 * The shop must keep working with none of it. Signing in is an option a fan
 * takes, not a gate they pass.
 */
const ACCESS = "pb-fan-token";
const REFRESH = "pb-fan-refresh";
const PROFILE = "pb-fan-profile";

const read = (key: string) => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		// Private browsing. The fan stays logged out, which still works.
		return null;
	}
};

const write = (key: string, value: string | null) => {
	try {
		if (value === null) window.localStorage.removeItem(key);
		else window.localStorage.setItem(key, value);
	} catch { /* nothing to do; they stay signed out */ }
};

export const FanSession = {
	token: () => read(ACCESS),

	refreshToken: () => read(REFRESH),

	/** Cached so the header can greet somebody without a round trip. */
	profile: () => {
		const raw = read(PROFILE);
		try {
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	},

	signedIn: () => !!read(ACCESS),

	set(body: any) {
		write(ACCESS, body?.accessToken || body?.token || null);
		write(REFRESH, body?.refreshToken || null);
		write(PROFILE, body?.fan ? JSON.stringify(body.fan) : null);
	},

	clear() {
		write(ACCESS, null);
		write(REFRESH, null);
		write(PROFILE, null);
	},

	/** The fan's id, for stamping an order. */
	fanId(): number | null {
		const profile = FanSession.profile();
		return profile?.id ?? null;
	}
};

/** Anonymous call — no token, no interceptor. */
const anon = async (path: string, init?: RequestInit) =>
	await fetch(`${environment.server.apiURL}/${path}`, {
		...init,
		headers: { "Content-Type": "application/json", ...(init?.headers || {}) }
	});

/**
 * A call as the signed-in fan.
 *
 * Retries once through the refresh token when the access token has lapsed —
 * fans come back fortnightly and the access token is far shorter-lived than
 * that, so an expired token is the normal case rather than an error.
 */
const asFan = async (path: string, init?: RequestInit): Promise<Response> => {
	const send = (token: string | null) =>
		fetch(`${environment.server.apiURL}/${path}`, {
			...init,
			headers: {
				"Content-Type": "application/json",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
				...(init?.headers || {})
			}
		});

	let response = await send(FanSession.token());
	if (response.status !== 401) return response;

	const refresh = FanSession.refreshToken();
	if (!refresh) return response;

	const renewed = await anon("FanAuth/Refresh", {
		body: JSON.stringify({ refreshToken: refresh }),
		method: "POST"
	});

	if (!renewed.ok) {
		// The refresh token has gone too. Sign them out rather than leave a
		// dead session that fails silently on every page.
		FanSession.clear();
		return response;
	}

	FanSession.set(await renewed.json());
	response = await send(FanSession.token());
	return response;
};

/** Supporter sign-in. */
export const FanAuthService = {
	login: async (emailAddress: string, password: string) =>
		await anon("FanAuth/Login", { body: JSON.stringify({ emailAddress, password }), method: "POST" }),

	register: async (data: any) =>
		await anon("FanAuth/Register", { body: JSON.stringify(data), method: "POST" }),

	me: async () => await asFan("FanAuth/Me"),

	myOrders: async (slug: string) => await asFan(`Checkout/MyOrders?clubSlug=${encodeURIComponent(slug)}`),

	/** Whether this fan can buy for a fixture yet — priority sales. */
	access: async (slug: string, fixtureId: number) =>
		await asFan(`Checkout/Access?clubSlug=${encodeURIComponent(slug)}&fixtureId=${fixtureId}`),

	/** The fan's own season tickets, and what they've lent out. */
	mySeasonTickets: async () => await asFan("SeasonTicket/Mine"),

	lend: async (seasonTicketId: number, fixtureId: number, data: any) =>
		await asFan(`SeasonTicket/Mine/${seasonTicketId}/Lend/${fixtureId}`, {
			body: JSON.stringify(data), method: "POST"
		}),

	withdrawLend: async (transferId: number) =>
		await asFan(`SeasonTicket/Mine/Transfers/${transferId}`, { method: "DELETE" })
};
